import React, { useCallback, useRef, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import {
	withConnectHost,
	isConnectDialogWebItem,
} from '@atlassian/jira-connect-utils/src/common/utils/ecosystem-connect-host.tsx';
import { useNavigationItemAnalytics } from '@atlassian/jira-horizontal-nav-common/src/common/analytics.tsx';
import { V4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getNav4Rollout } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-nav4-rollout/index.tsx';
import AppItemIcon from '@atlassian/jira-horizontal-nav-icons/src/ui/app-item-icon/index.tsx';
import type { AppTab_tab$key } from '@atlassian/jira-relay/src/__generated__/AppTab_tab.graphql';
import type { AppTab_container$key } from '@atlassian/jira-relay/src/__generated__/AppTab_container.graphql';
import { AppTabMenu } from './AppTabMenu.tsx';
import { AppEnvLozenge } from './AppEnvLozenge.tsx';
import { TabWithMenu } from './TabWithMenu.tsx';

type Props = {
	tabFragment: AppTab_tab$key;
	containerFragment: AppTab_container$key;
	position: number;
	beforeItemId: string | null;
	afterItemId: string | null;
};

export const AppTab = ({
	tabFragment,
	containerFragment,
	position,
	beforeItemId,
	afterItemId,
}: Props): React.JSX.Element => {
	const webItemRef = useRef<HTMLElement>(null);

	const tabData = useFragment(
		graphql`
			fragment AppTab_tab on JiraAppNavigationItem {
				...TabWithMenu_tab
				...AppTabMenu_tab
				label @required(action: THROW) # or the tab is unusable
				url @required(action: THROW) # or the tab is unusable
				appId
				iconUrl
				styleClass
				envLabel
				sections {
					label
				}
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment AppTab_container on JiraContainerNavigationResult {
				...TabWithMenu_container
				...AppTabMenu_container
			}
		`,
		containerFragment,
	);

	const triggerAnalytics = useNavigationItemAnalytics(
		useMemo(
			() => ({
				itemType: 'app',
				itemId: tabData.appId ?? '',
				navState: V4,
				stage: getNav4Rollout().stage,
				navigationLayer: CONTAINER,
				navigationContainer: TAB,
				isInMore: false,
			}),
			[tabData.appId],
		),
	);

	const onClick = useCallback(
		(e: Event) => {
			if (tabData.styleClass) {
				withConnectHost((connectHost) => {
					if (webItemRef.current && tabData.url) {
						connectHost.triggerWebItem(webItemRef.current, tabData.styleClass ?? '', tabData.url);
					}
				});

				if (isConnectDialogWebItem(tabData.styleClass)) {
					e.preventDefault();
					e.stopPropagation();
				}
			}

			triggerAnalytics();
		},
		[tabData.styleClass, tabData.url, triggerAnalytics],
	);

	if (tabData.sections && tabData.sections.length > 0) {
		return (
			<AppTabMenu
				icon={<AppItemIcon url={tabData.iconUrl || ''} size="small" />}
				elemAfter={<AppEnvLozenge label={tabData.envLabel || ''} />}
				tabFragment={tabData}
				containerFragment={containerData}
				testId={`horizontal-nav.app-tab.${tabData.label}`}
				position={position}
				onClick={triggerAnalytics}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
			/>
		);
	}

	return (
		<TabWithMenu
			elemBefore={<AppItemIcon url={tabData.iconUrl || ''} size="small" />}
			elemAfter={<AppEnvLozenge label={tabData.envLabel || ''} />}
			href={tabData.url}
			tabFragment={tabData}
			containerFragment={containerData}
			onClick={onClick}
			testId={`horizontal-nav.app-tab.${tabData.label}`}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			position={position}
			type="router-link"
			ref={webItemRef}
		/>
	);
};

export default AppTab;
